import axios, {AxiosResponse} from "axios";
import {CashFlow} from "@/model/CashFlow";
import {SpringPagedResult} from "@/service/PagedResult";

const PATH = "/statistics";
const PATH_V2 = "/v2/statistics";
const OPERATOR_PERFORMANCES_PAGE_SIZE = 200;

/**
 * Positions and worked minutes; `positionsPerHour` is calculated from both and absent without minutes.
 */
export interface CountingDto {
    workedMinutes?: number;
    positions?: number;
    positionsPerHour?: number;
}

export interface OperatorPerformanceDto {
    employee: {
        id: string;
        employeeNumber: number;
        firstName: string;
        lastName: string;
        companyLocation?: string;
    };
    total: CountingDto;
    medianPositionsPerHour?: number;
    positionsPerHourStandardDeviation?: number;
}

/**
 * One inventory an operator counts on: their `counting` (minutes worked in the month, positions on the whole
 * inventory) and the `orderTotal` of all operators' positions and all employees' minutes on it.
 */
export interface OperatorPerformanceOrder {
    order: {
        id: string;
        name: string;
        validFrom: string;
        validTo?: string;
    };
    counting: CountingDto;
    qualityLevel?: "GREEN" | "YELLOW" | "RED";
    orderTotal: CountingDto;
}

export interface OperatorPerformanceResponse {
    month: string;
    performance: OperatorPerformanceDto;
    orders: Array<OperatorPerformanceOrder>;
}

function cashFlowRequest(cashFlow: CashFlow): CashFlow {
    return {
        type: cashFlow.type,
        month: cashFlow.month,
        amount: cashFlow.amount,
        description: cashFlow.description || undefined
    };
}

export default {
    /**
     * Gets an operator's performance in a month and the inventories it is calculated from via the v2 API by the
     * employee's uuid. Callers who may not see the statistics overview may only get their own.
     */
    getOperatorPerformance(employeeUuid: string, month: string): Promise<OperatorPerformanceResponse> {
        return axios.get(`${PATH_V2}/operator-performances/${employeeUuid}`, {
            params: {
                month: month
            }
        }).then(response => response.data);
    },
    createCashFlow (orderUuid: string, cashFlow: CashFlow): Promise<AxiosResponse<CashFlow>> {
        return axios.post(`/v2/orders/${orderUuid}/cash-flows`, cashFlowRequest(cashFlow));
    },
    updateCashFlow (orderUuid: string, cashFlowId: string, cashFlow: CashFlow): Promise<AxiosResponse<CashFlow>> {
        return axios.put(`/v2/orders/${orderUuid}/cash-flows/${cashFlowId}`, cashFlowRequest(cashFlow));
    },
    deleteCashFlow (orderUuid: string, cashFlowId: string) {
        return axios.delete(`/v2/orders/${orderUuid}/cash-flows/${cashFlowId}`);
    },
    getCashFlows (orderUuid: string): Promise<AxiosResponse<{ cashFlows: Array<CashFlow> }>> {
        return axios.get(`/v2/orders/${orderUuid}/cash-flows`);
    },
    /**
     * Gets the performances of all operators in a month via the v2 API, sorted by employee number, by loading all
     * of its pages.
     */
    getOperatorPerformances(month: string): Promise<Array<OperatorPerformanceDto>> {
        const loadFrom = (page: number, loaded: Array<OperatorPerformanceDto>): Promise<Array<OperatorPerformanceDto>> =>
            axios.get<SpringPagedResult<OperatorPerformanceDto>>(`${PATH_V2}/operator-performances`, {
                params: {
                    month: month,
                    page: page,
                    size: OPERATOR_PERFORMANCES_PAGE_SIZE
                }
            }).then(response => {
                const all = loaded.concat(response.data.content);
                return page + 1 < response.data.page.totalPages ? loadFrom(page + 1, all) : all;
            });
        return loadFrom(0, []);
    },
    getDailyStatistic(orderIds: Array<number>, employeeIds: Array<number>, date: string) {
        return axios.get(`${PATH}/employees/daily`, {
            params: {
                orderIds: orderIds.join(","),
                employeeIds: employeeIds.join(","), 
                date: date
            }
        });
    }
};
